/**
 * La sesión de comprador compartida, vista desde el root-config (`PLAN-SESION-UNIFICADA.md`).
 *
 * La reconciliación de verdad vive en `@jopitnow/web-session` (`reconcileBuyerSession`,
 * `watchBuyerSession`). Acá está sólo lo que el root-config necesita decidir ANTES de bajar ese
 * chunk: si vale la pena bloquear `start()` por ella, y con qué opciones llamarla. Es un módulo
 * aparte y sin I/O para que se pueda probar sin `window` ni webpack.
 */

/**
 * El ambiente que la sesión de comprador conoce. El bundle sólo distingue dos: `NODE_ENV` es
 * `production` en el build de producción y `development` en el de staging (ver `build:staging`
 * en package.json), y un build local habla con la API de staging.
 */
export function environmentOf(nodeEnv) {
    return nodeEnv === "production" ? "production" : "staging";
}

/**
 * Espejo de `buyerSessionHintCookieName` de `@jopitnow/web-session`, duplicado a propósito: el
 * prefiltro de abajo corre antes del `import()` del chunk, así que no puede pedírselo. Si cambia
 * allá, cambia acá.
 */
export function buyerSessionHintCookieName(environment) {
    return environment === "production" ? "jopit_buyer_uid" : "jopit_buyer_uid_staging";
}

/**
 * ¿Hay un hint de la sesión compartida para ESTE ambiente en el frasco? Cualquier valor cuenta,
 * el tombstone incluido: las dos cosas que el hint puede pedir —firmar la sesión que falta, o
 * cerrar la que sobra— cambian lo que la página va a pintar, y eso es lo que decide si la
 * reconciliación bloquea `start()` o corre después.
 *
 * Mira una cadena que ya está en memoria y nada más. En el 100% de las cargas sin hint —hoy,
 * todas— esto es lo único que corre antes de arrancar.
 */
export function hasBuyerSessionHint(cookieHeader, environment) {
    const prefix = `${buyerSessionHintCookieName(environment)}=`;
    return String(cookieHeader || "")
        .split(";")
        .some((part) => part.trim().startsWith(prefix));
}

/**
 * Las opciones con las que el root-config llama a `reconcileBuyerSession` y `watchBuyerSession`.
 *
 * `api` es el request anónimo (`onAppRequest`) —mint y logout van autenticados por la cookie, no
 * por un Bearer— y `authApi` el autenticado (`onAuthRequest`) para el enroll, que es cómo un login
 * de Firebase se vuelve la cookie compartida. Las dos van con `credentials: "include"` desde
 * adentro de web-session. `sameOrigin` no se pasa: el default (un `fetch` al propio host) es lo
 * que un tenant necesita para la ruta del Worker.
 */
export function buyerSessionOptionsFor(consumer, environment, onError) {
    return {
        environment,
        requests: {
            api: (endpoint, init) => consumer.onAppRequest(endpoint, init),
            authApi: (endpoint, init) => consumer.onAuthRequest(endpoint, init),
        },
        onError,
    };
}
